import { useCallback } from 'react';

import {
  useDropJobApplicationMutation,
  useMoveJobApplicationMutation,
  useReviseJobApplicationMutation,
  useTrackJobApplicationMutation,
} from 'core/graphql';

import { CareerApplicationStatus } from './transform';

export interface ICareerApplicationInput {
  vacancyId?: string | null;
  companyName?: string;
  position?: string;
  applyUrl?: string;
  status?: string;
  appliedAt?: string | null;
  nextActionAt?: string | null;
  note?: string;
}

export interface IApplicationActionResult {
  success: boolean;
  /** Штатный ответ, не ошибка: отклик на эту вакансию уже в работе. */
  duplicate: boolean;
  applicationId: string | null;
  errorCode: string | null;
}

/**
 * Действия над журналом поиска работы (ТЗ 0069).
 *
 * 🔴 Каждая мутация перезапрашивает `CareerJourneyBoard`: отклик двигает сразу
 * два этапа маршрута («Отклики» и «Интервью»), готовность и сводку на
 * дорожной карте. Считать эти числа на клиенте нельзя — они принадлежат
 * серверу, и вторая копия арифметики разъедется.
 *
 * ⚠️ `refetchQueries` по ИМЕНИ операции — тихий no-op, если запрос не
 * смонтирован. Он живёт в `CareerLayout`, то есть на всех экранах раздела;
 * см. комментарий там.
 */
const REFETCH = ['CareerJourneyBoard', 'CareerApplicationsList'];

export const useCareerApplicationActions = () => {
  const [track, { loading: tracking }] = useTrackJobApplicationMutation();
  const [revise, { loading: revising }] = useReviseJobApplicationMutation();
  const [move, { loading: moving }] = useMoveJobApplicationMutation();
  const [drop, { loading: dropping }] = useDropJobApplicationMutation();

  const trackApplication = useCallback(
    async (input: ICareerApplicationInput): Promise<IApplicationActionResult> => {
      const { data } = await track({
        variables: { input },
        refetchQueries: REFETCH,
      });
      const payload = data?.createCareerApplication;
      return {
        success: Boolean(payload?.success),
        duplicate: Boolean(payload?.duplicate),
        applicationId: payload?.application?.id ?? null,
        errorCode: payload?.customErrors?.[0]?.code ?? null,
      };
    },
    [track],
  );

  const reviseApplication = useCallback(
    async (id: string, input: ICareerApplicationInput): Promise<IApplicationActionResult> => {
      const { data } = await revise({
        variables: { id, input },
        refetchQueries: REFETCH,
      });
      const payload = data?.updateCareerApplication;
      return {
        success: Boolean(payload?.success),
        duplicate: false,
        applicationId: payload?.application?.id ?? null,
        errorCode: payload?.customErrors?.[0]?.code ?? null,
      };
    },
    [revise],
  );

  const moveApplication = useCallback(
    async (id: string, status: CareerApplicationStatus): Promise<IApplicationActionResult> => {
      const { data } = await move({
        variables: { id, status },
        refetchQueries: REFETCH,
      });
      const payload = data?.setCareerApplicationStatus;
      return {
        success: Boolean(payload?.success),
        duplicate: false,
        applicationId: payload?.application?.id ?? null,
        errorCode: payload?.customErrors?.[0]?.code ?? null,
      };
    },
    [move],
  );

  const dropApplication = useCallback(
    async (id: string): Promise<IApplicationActionResult> => {
      const { data } = await drop({
        variables: { id },
        refetchQueries: REFETCH,
      });
      const payload = data?.deleteCareerApplication;
      return {
        success: Boolean(payload?.success),
        duplicate: false,
        applicationId: null,
        errorCode: payload?.customErrors?.[0]?.code ?? null,
      };
    },
    [drop],
  );

  return {
    trackApplication,
    reviseApplication,
    moveApplication,
    dropApplication,
    loading: tracking || revising || moving || dropping,
  };
};
